import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  setCitiesList,
  setTripsList,
  setSeatsList,
  setCreatedCart,
  setFetchedCart,
  setUpdatedCart,
} from "../Redux/Slices/BusBookingSlice";
import Config from "../Config";

export const BusBookingService = createApi({
  reducerPath: "BusBookingService",
  baseQuery: RtkBaseQueryWithReauth(Config.BASE_URL),
  tagTypes: ["BusBooking"],
  endpoints: (builder) => ({
    getCitiesList: builder.query<any, void>({
      query: () => ({
        url: `${ApiPath.getCitiesList}`,
        method: "GET",
      }),
      providesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data }: any = await queryFulfilled;
          dispatch(setCitiesList(data.data));
        } catch (error) {
          console.error("Error fetching cities list:", error);
        }
      },
    }),

    getTripsList: builder.query<
      any,
      {
        fromCityId: string;
        fromCityName: string;
        toCityId: string;
        toCityName: string;
        departureDate: string;
        returnDate?: string;
        passengers: string;
      }
    >({
      query: ({
        fromCityId,
        fromCityName,
        toCityId,
        toCityName,
        departureDate,
        returnDate,
        passengers,
      }) => ({
        url: ApiPath.getTripsList,
        method: "POST",
        body: {
          from_city_id: fromCityId,
          from_city_name: fromCityName,
          to_city_id: toCityId,
          to_city_name: toCityName,
          departure_date: departureDate,
          return_date: returnDate || null,
          passengers: passengers,
        },
      }),
      providesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data }: any = await queryFulfilled;
          dispatch(setTripsList(data.data));
        } catch (error) {
          console.error("Error fetching trips list:", error);
        }
      },
    }),

    /**
     * The stops, per-leg times and the transfer window for one journey.
     *
     * Not part of the search: a Berlin to Paris search returns 58 journeys and
     * 49 have a change, so fetching this for all of them would be 49 calls for
     * a list nobody reads in full. Asked for once the customer has chosen.
     */
    getJourneyDetail: builder.query<
      any,
      {
        fromCityId: string;
        toCityId: string;
        departureDate: string;
        departureTs: number;
        arrivalTs: number;
        rideIds?: string[];
      }
    >({
      query: (body) => ({
        url: ApiPath.tripJourneyDetail,
        method: "POST",
        body,
      }),
      transformResponse: (r: any) => r?.data?.legs || [],
    }),

    getSeatsList: builder.query<
      any,
      {
        fromCityId: string;
        toCityId: string;
        tripId: string;
      }
    >({
      query: ({ fromCityId, toCityId, tripId }) => ({
        url: ApiPath.getSeatsList,
        method: "POST",
        body: {
          from_city_id: fromCityId,
          to_city_id: toCityId,
          trip_id: tripId,
        },
      }),
      providesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data }: any = await queryFulfilled;
          dispatch(setSeatsList(data.data));
        } catch (error) {
          console.error("Error fetching seats:", error);
        }
      },
    }),

    createBusCart: builder.mutation<
      any,
      {
        currency: string;
      }
    >({
      query: ({ currency }) => ({
        url: ApiPath.createCartList,
        method: "POST",
        body: {
          currency: currency,
        },
      }),
      invalidatesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data) {
            dispatch(setCreatedCart(response.data));
          } else {
            console.warn("Received empty created cart data");
          }
        } catch (error) {
          console.error("Error creating cart:", error);
        }
      },
    }),

    getBusCart: builder.query<any, { cartId: string }>({
      query: ({ cartId }) => ({
        url: `${ApiPath.getCartList}?cartId=${cartId}`, // Fixed: Pass cartId as a query param
        method: "GET",
      }),
      providesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data) {
            dispatch(setFetchedCart(response.data));
          } else {
            console.warn("Received empty fetched cart data");
          }
        } catch (error) {
          console.error("Error fetching cart:", error);
        }
      },
    }),

    addTicketToBusCart: builder.mutation<
      any,
      {
        cartId: string;
        trip: string;
        passengerTypes: { adult: number; children: number };
      }
    >({
      query: ({ cartId, trip, passengerTypes }) => ({
        url: ApiPath.addTicketToBusCart,
        method: "POST",
        body: {
          cartId,
          trip,
          passengerTypes,
        },
      }),
      invalidatesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data) {
            dispatch(setUpdatedCart(response.data));
          } else {
            console.warn("Received empty cart update data");
          }
        } catch (error) {
          // console.error("Error adding ticket to cart:", error);
        }
      },
    }),

    deleteTicketFromBusCart: builder.mutation<
      any,
      { cartId: string; ticketId: string }
    >({
      query: ({ cartId, ticketId }) => ({
        url: ApiPath.deleteTicketFromBusCart,
        method: "DELETE",
        body: { cartId, ticketId },
      }),
      invalidatesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data) {
            dispatch(setUpdatedCart(response.data));
          } else {
            console.warn("Received empty cart update data");
          }
        } catch (error) {
          console.error("Error deleting ticket from cart:", error);
        }
      },
    }),

    addSeatReservationList: builder.mutation<
      any,
      { cartId: string; reservations: any[] }
    >({
      query: ({ cartId, reservations }) => ({
        url: ApiPath.addSeatReservationList,
        method: "PUT",
        body: {
          cartId,
          reservations,
        },
      }),
      invalidatesTags: ["BusBooking"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data) {
            dispatch(setUpdatedCart(response.data));
          } else {
            console.warn("Received empty response for seat reservation");
          }
        } catch (error) {
          console.error("Error adding seat reservation:", error);
        }
      },
    }),

    deleteSeatReservation: builder.mutation<
      any,
      { cartId: string; ticketId: string }
    >({
      query: ({ cartId, ticketId }) => ({
        url: `/deleteseatfromreservation/${cartId}/${ticketId}`,
        method: "DELETE",
      }),
    }),

   cancelOrder: builder.mutation<
  any,
  { orderId: string; token: string; orderNumber: string; body: any }
>({
  query: ({ orderId, token, orderNumber, body }) => ({
    url: `/cancelorder/${orderId}/${token}/${orderNumber}`,
    method: "PUT",
    body, // <- pass request body here
  }),
}),


    getLuggageDetails: builder.mutation<
      any,
      {
        trip_uid: string;
        children: number;
        adult: number;
      }
    >({
      query: (body) => ({
        url: ApiPath.getLuggageDetails,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["BusBooking"],
    }),

    updateLuggage: builder.mutation<
      any,
      { cartId: string; ticket: string; amount: number; luggageType: string }
    >({
      query: ({ cartId, ticket, amount, luggageType }) => ({
        url: `/cart/${cartId}/luggage`,
        method: "PUT",
        body: { ticket, amount, luggageType },
      }),
    }),
  }),
});

export const {
  useLazyGetCitiesListQuery,
  useLazyGetTripsListQuery,
  useLazyGetSeatsListQuery,
  useLazyGetJourneyDetailQuery,
  useCreateBusCartMutation,
  useLazyGetBusCartQuery,
  useAddTicketToBusCartMutation,
  useDeleteTicketFromBusCartMutation,
  useAddSeatReservationListMutation,
  useGetLuggageDetailsMutation,
  useDeleteSeatReservationMutation,
  useCancelOrderMutation,
  useUpdateLuggageMutation,
} = BusBookingService;
